/**
 * lesson-api.ts
 * Client for the TeachBud AI Lesson Generation API (artifacts/api-server).
 * The API server is mounted at /api in the proxy, so all requests go to /api/lesson/...
 */

const API_BASE = '/api/lesson';

/**
 * Thrown when the API returns 402 Payment Required — the teacher needs a
 * subscription or trial before generating resources.  Components should catch
 * this and show an upgrade prompt rather than the generic "check connection" error.
 */
export class SubscriptionRequiredError extends Error {
  readonly code: 'subscription_required' | 'subscription_expired';
  constructor(message: string, code: 'subscription_required' | 'subscription_expired' = 'subscription_required') {
    super(message);
    this.name = 'SubscriptionRequiredError';
    this.code = code;
  }
}

/** Shape of the raw content the AI returns. All fields are optional
 *  because the API falls back gracefully; assembleAiLesson fills defaults. */
export interface AiLessonContent {
  title?: string;
  topic?: string;
  objective?: string;
  successCriteria?: string;
  vocabulary?: string;
  materials?: string;
  warmUp?: string;
  directInstruction?: string;
  guidedPractice?: string;
  independentPractice?: string;
  formativeAssessment?: string;
  exitTicket?: string;
  differentiation?: string;
  extensionActivity?: string;
  homework?: string;
  teacherPrep?: string;
  requiredMaterials?: string;
  optionalMaterials?: string;
  reflectionNotes?: string;
  tieredPractice?: { belowLevel: string; onLevel: string; aboveLevel: string };
  [key: string]: unknown;
}

export interface GenerateLessonRequest {
  skillToTeach: string;
  grade: string;
  subject: string;
  standard: string;
  duration: string;
  instructions: string;
  /** Specific strategy/method/model required (e.g. "partial quotients", "area model") */
  requiredStrategy?: string;
  /** Custom learning objective — defaults to "Students will be able to [skill]." */
  learningObjective?: string;
  tieredPractice?: boolean;
}

/**
 * Attempt a single fetch to the lesson generation endpoint.
 * Throws on network error or non-2xx response.
 */
async function attemptFetch(
  inputs: GenerateLessonRequest,
  userId?: string,
): Promise<AiLessonContent> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  // userId is no longer sent as a header — Clerk session cookie carries auth.
  void userId;

  const response = await fetch(`${API_BASE}/generate`, {
    method: 'POST',
    headers,
    body: JSON.stringify(inputs),
  });

  if (!response.ok) {
    let message = `HTTP ${response.status}`;
    let errorCode = '';
    try {
      const body = await response.json() as { error?: string; message?: string };
      errorCode = body.error ?? '';
      // Prefer the human-readable message field; fall back to error code
      message = body.message ?? body.error ?? message;
    } catch { /* ignore */ }
    if (response.status === 402) {
      throw new SubscriptionRequiredError(
        message,
        errorCode === 'subscription_expired' ? 'subscription_expired' : 'subscription_required',
      );
    }
    throw new Error(message);
  }

  return response.json() as Promise<AiLessonContent>;
}

export interface RegenerateSectionRequest extends GenerateLessonRequest {
  /** The lesson field key to regenerate (e.g. "tieredPractice", "warmUp", "exitTicket") */
  section: string;
  /** Human-readable explanation of why this section needs regeneration */
  reason?: string;
  /** Current lesson content supplied as context so the AI can stay consistent */
  currentContent?: Record<string, unknown>;
}

/**
 * Ask the API to regenerate a single named section that failed extraction.
 * Returns an object keyed by the section name with the corrected content.
 */
export async function regenerateSectionAI(
  req: RegenerateSectionRequest,
): Promise<Record<string, unknown>> {
  const response = await fetch(`${API_BASE}/regenerate-section`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  });

  if (!response.ok) {
    let message = `HTTP ${response.status}`;
    try {
      const body = await response.json() as { error?: string };
      if (body.error) message = body.error;
    } catch { /* ignore */ }
    throw new Error(message);
  }

  return response.json() as Promise<Record<string, unknown>>;
}

// ── Standards alignment check ─────────────────────────────────────────────────

export type AlignmentClassification = 'Direct' | 'Partial' | 'Prerequisite' | 'Extension' | 'Mismatch';

export interface AlignmentCheckResult {
  classification: AlignmentClassification;
  reason: string;
  suggestion: string | null;
}

/**
 * Fast pre-generation check: compare skill statement vs entered standard.
 * Returns a classification and advisory reason. Never blocks generation.
 * Throws on network error or non-2xx — caller handles gracefully.
 */
export async function checkAlignmentAI(
  skillToTeach: string,
  standard: string,
  grade: string,
  subject: string,
): Promise<AlignmentCheckResult> {
  const response = await fetch(`${API_BASE}/check-alignment`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ skillToTeach, standard, grade, subject }),
  });
  if (!response.ok) {
    let message = `HTTP ${response.status}`;
    try { const b = await response.json() as { error?: string }; if (b.error) message = b.error; } catch { /* ignore */ }
    throw new Error(message);
  }
  return response.json() as Promise<AlignmentCheckResult>;
}

// ── Pacing validation ─────────────────────────────────────────────────────────

export interface PacingSectionSummary {
  type: string;
  wordCount: number;
  itemCount: number;
}

export interface PacingCheckRequest {
  duration: string;
  grade: string;
  subject: string;
  tieredPractice?: boolean;
  sections: Record<string, PacingSectionSummary>;
}

export interface PacingCheckResult {
  assessment: 'realistic' | 'tight' | 'overloaded';
  estimatedMinutes: number;
  requestedMinutes: number;
  breakdown: Record<string, number>;
  summary: string;
  recommendations: string[];
  suggestedDuration: string | null;
}

/**
 * Post-generation pacing check: estimate realistic instructional time based on
 * actual lesson content density and compare to the requested duration.
 * Never blocks display — call in the background after lesson generation completes.
 */
export async function checkPacingAI(req: PacingCheckRequest): Promise<PacingCheckResult> {
  const response = await fetch(`${API_BASE}/check-pacing`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  });
  if (!response.ok) {
    let message = `HTTP ${response.status}`;
    try { const b = await response.json() as { error?: string }; if (b.error) message = b.error; } catch { /* ignore */ }
    throw new Error(message);
  }
  return response.json() as Promise<PacingCheckResult>;
}

/**
 * Call the AI lesson generation endpoint with automatic retries.
 *
 * Retries up to 2 times on transient errors (network failures or 5xx responses).
 * Client errors (4xx) are not retried — they indicate a bad request.
 *
 * Throws a descriptive Error if all attempts fail so the caller can show a
 * meaningful message to the teacher instead of silently falling back.
 */
export async function generateLessonAI(
  inputs: GenerateLessonRequest,
  userId?: string,
): Promise<AiLessonContent> {
  const MAX_ATTEMPTS = 3;
  const RETRY_DELAY_MS = [1500, 3000]; // delay before attempt 2, delay before attempt 3

  let lastError: Error | null = null;

  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    try {
      const result = await attemptFetch(inputs, userId);
      if (attempt > 1) {
        console.info(`[TeachBud] AI generation succeeded on attempt ${attempt}`);
      }
      return result;
    } catch (err) {
      lastError = err instanceof Error ? err : new Error(String(err));

      // Don't retry client errors (bad request, unauthorized, not found, payment required)
      if (lastError instanceof SubscriptionRequiredError) {
        throw lastError;
      }
      const msg = lastError.message;
      if (/^HTTP 4\d\d/.test(msg)) {
        console.error(`[TeachBud] AI generation failed with client error (no retry): ${msg}`);
        throw lastError;
      }

      if (attempt < MAX_ATTEMPTS) {
        const delay = RETRY_DELAY_MS[attempt - 1] ?? 3000;
        console.warn(`[TeachBud] AI generation attempt ${attempt} failed — retrying in ${delay}ms. Error: ${msg}`);
        await new Promise(resolve => setTimeout(resolve, delay));
      } else {
        console.error(`[TeachBud] AI generation failed after ${MAX_ATTEMPTS} attempts: ${msg}`);
      }
    }
  }

  throw lastError ?? new Error('AI lesson generation failed — please try again.');
}

// ── Assessment generation ─────────────────────────────────────────────────────

import type { AssessmentConfig, GeneratedAssessment } from './assessment-engine';
import type { GeneratedLesson } from './lesson-engine';

/**
 * Generate an assessment derived from a lesson.
 * Every question traces back to a specific lesson section.
 */
export async function generateAssessmentAI(
  lesson: GeneratedLesson,
  config: AssessmentConfig,
): Promise<GeneratedAssessment> {
  const response = await fetch(`${API_BASE}/generate-assessment`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ lesson, config }),
  });
  if (!response.ok) {
    let message = `HTTP ${response.status}`;
    let errorCode = '';
    try { const b = await response.json() as { error?: string; message?: string }; errorCode = b.error ?? ''; message = b.message ?? b.error ?? message; } catch { /* ignore */ }
    if (response.status === 402) throw new SubscriptionRequiredError(message, errorCode === 'subscription_expired' ? 'subscription_expired' : 'subscription_required');
    throw new Error(message);
  }
  return response.json() as Promise<GeneratedAssessment>;
}

// ── Presentation generation ───────────────────────────────────────────────────

import type { PresentationConfig, GeneratedPresentation, PresentationSlide } from './presentation-engine';

/**
 * Generate a classroom presentation from a lesson.
 * Returns a structured slide model — single AI call, multiple render targets.
 */
export async function generatePresentationAI(
  lesson: GeneratedLesson,
  config: PresentationConfig,
): Promise<GeneratedPresentation> {
  const response = await fetch(`${API_BASE}/generate-presentation`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ lesson, config }),
  });
  if (!response.ok) {
    let message = `HTTP ${response.status}`;
    let errorCode = '';
    try { const b = await response.json() as { error?: string; message?: string }; errorCode = b.error ?? ''; message = b.message ?? b.error ?? message; } catch { /* ignore */ }
    if (response.status === 402) throw new SubscriptionRequiredError(message, errorCode === 'subscription_expired' ? 'subscription_expired' : 'subscription_required');
    throw new Error(message);
  }
  return response.json() as Promise<GeneratedPresentation>;
}

/**
 * Regenerate a single slide based on teacher preferences.
 */
export async function regenerateSlideAI(
  lesson: GeneratedLesson,
  slide: PresentationSlide,
  preferences: string[],
): Promise<PresentationSlide> {
  const response = await fetch(`${API_BASE}/regenerate-slide`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ lesson, slide, preferences }),
  });
  if (!response.ok) {
    let message = `HTTP ${response.status}`;
    try { const b = await response.json() as { error?: string }; if (b.error) message = b.error; } catch { /* ignore */ }
    throw new Error(message);
  }
  return response.json() as Promise<PresentationSlide>;
}
